import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, CircularProgress, Alert, Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  PlaylistAdd as QueueIcon,
  PlaylistRemove as NoQueueIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { rxService } from '../../services/rxService';
import api from '../../services/api';
import { ISEC_QUESTIONS, ISEC_SUMMARY_KEY } from '../../constants/isecQuestions';
import { headerStripSurface } from '../../theme/gridSurfaces';
import OrderVerificationSummary from '../common/OrderVerificationSummary';
import {
  deriveOrderVerificationSteps,
  type VerifiableOrder,
  type VerificationSignature,
} from '../../utils/orderVerificationSteps';

// ---- Types ----

interface DrugVerification {
  rxNumber?: string;
  medicationName?: string;
  din?: string;
  strength?: string;
  daysSupply?: number;
  verifiedDaysSupply?: number;
  counselFlags?: Record<string, boolean>;
  clinicalNotes?: Record<string, string>; // Per-ISEC-letter notes + 'summary' counselling note
  verifiedBy?: string;
  verifiedAt?: string;
}

/**
 * GP-1439 — the per-Rx "Send to task queue" decision the pharmacist made on the
 * Clinical Verification IESC panel, persisted on
 * `metadata.clinicalVerification.taskQueueRouting`. Surfaced here so a later
 * reviewer can tell why an order did or did not reach the counselling task queue
 * (see components/clinical-verification/taskQueueRouting.ts).
 */
interface TaskQueueRoutingDecision {
  sendToTaskQueue: boolean;
  defaultedTo?: boolean;
  overridden?: boolean;
  decidedBy?: string;
  decidedAt?: string;
  policySource?: string;
}

interface VerificationTabProps {
  orderId: string;
  orderNumber?: string;
  pharmacyId: string;
  patientId: string;
}

// ---- Constants ----

const COUNSEL_FLAG_LABELS: Record<string, string> = {
  didCounsel: 'DID Counsel',
  notCounsel: 'NOT Counsel',
  inPerson: 'In Person',
  viaPhone: 'Via Phone',
  agent: 'Agent',
  patRefused: 'Pat Refused',
  language: 'Language',
  delivered: 'Delivered',
};

const SIGNATURE_LABELS: Record<string, string> = {
  data_verification: 'Data',
  clinical_verification: 'Clinical',
  product_verification: 'Product',
  dispensing: 'Dispensing',
};

const SIGNATURE_COLORS: Record<string, string> = {
  data_verification: '#1976d2',
  clinical_verification: '#198754',
  product_verification: '#ed6c02',
  dispensing: '#9c27b0',
};

// ---- Component ----

const VerificationTab: React.FC<VerificationTabProps> = ({
  orderId,
  orderNumber,
  pharmacyId,
  patientId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<VerifiableOrder | null>(null);
  const [drugVerifications, setDrugVerifications] = useState<DrugVerification[]>([]);
  const [signatures, setSignatures] = useState<VerificationSignature[]>([]);
  // GP-1387 review: signature-only steps (Data Verification, Dispensing) exist
  // in the step list ONLY via signature rows, so a failed signatures fetch must
  // surface as an error — rendering the audit without them silently shows a
  // data-verified, dispensed order as if those steps never happened.
  const [signaturesFailed, setSignaturesFailed] = useState(false);
  const [verifiedBy, setVerifiedBy] = useState<string>('');
  const [verifiedAt, setVerifiedAt] = useState<string>('');
  const [taskQueueRouting, setTaskQueueRouting] = useState<
    Record<string, TaskQueueRoutingDecision>
  >({});

  useEffect(() => {
    if (!orderId || !pharmacyId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch the order. Every verification step's record lives on it —
        //    clinical/product verification metadata, the packaging stamp and the
        //    mirrored *_verified_by/_at columns (see utils/orderVerificationSteps).
        const orderRes = await api.get(`/api/orders/${pharmacyId}/${orderId}`);
        const orderData = orderRes.data?.data || orderRes.data;
        setOrder(orderData || null);

        const clinicalVerification = orderData?.metadata?.clinicalVerification;
        const verifications: DrugVerification[] = clinicalVerification?.drugVerifications || [];
        setDrugVerifications(verifications);
        setVerifiedBy(clinicalVerification?.verifiedBy || '');
        setVerifiedAt(clinicalVerification?.verifiedAt || '');
        setTaskQueueRouting(clinicalVerification?.taskQueueRouting || {});

        // 2. Batch fetch signatures for every Rx on the order. GP-1387: this used
        //    to run only when clinicalVerification existed, so an order verified
        //    at any OTHER step (data check, packaging, product verification)
        //    showed nothing at all. The Rx list falls back to the billing
        //    medications / the order's own Rx so signatures still resolve before
        //    clinical verification has been completed.
        const rxNumbers = Array.from(
          new Set(
            [
              ...verifications.map((v) => v.rxNumber),
              ...(orderData?.metadata?.billingData?.medications || []).map(
                (m: any) => m?.rxNumber,
              ),
              orderData?.rxNumber,
            ]
              .map((rx) => (rx == null ? '' : String(rx).trim()))
              .filter(Boolean),
          ),
        );

        if (rxNumbers.length > 0) {
          try {
            // A FLAT list across every requested Rx, each row carrying its own
            // rxNumber — not a map keyed by Rx. Reading it as a map is what left
            // every signature slot blank (GP-1387); rxService now unwraps it.
            setSignatures(await rxService.getSignaturesBatch(pharmacyId, rxNumbers));
            setSignaturesFailed(false);
          } catch {
            // Never render a partial audit as if it were complete — flag it so
            // the history section shows a load error instead (no silent gap).
            console.warn('Failed to load signatures batch');
            setSignaturesFailed(true);
          }
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || 'Failed to load verification data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [orderId, pharmacyId]);

  // Per-Rx index for the drug cards below; the step audit uses the flat list.
  const signaturesMap = useMemo(() => {
    const map: Record<string, VerificationSignature[]> = {};
    for (const signature of signatures) {
      const rx = signature?.rxNumber == null ? '' : String(signature.rxNumber);
      if (!rx) continue;
      (map[rx] = map[rx] || []).push(signature);
    }
    return map;
  }, [signatures]);

  const verificationSteps = useMemo(
    () => deriveOrderVerificationSteps(order, signatures),
    [order, signatures],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <Stack spacing={1} alignItems="center">
          <CircularProgress size={32} />
          <Typography variant="caption" color="text.secondary">Loading verification data...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  // GP-1387: the tab used to bail out here whenever `metadata.clinicalVerification`
  // was missing, so an order verified at ANY other step (data check, packaging,
  // product verification) read "No verification data yet" while its signatures and
  // stage stamps sat on the very order it had just fetched. Only claim there is
  // nothing to show when no step has been recorded AND no drug was clinically
  // verified.
  if (verificationSteps.length === 0 && drugVerifications.length === 0) {
    return (
      <Box
        data-testid="verification-tab-empty"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}
      >
        <VerifiedIcon sx={{ fontSize: 48, color: 'grey.300' }} />
        <Typography variant="body1" color="text.secondary">No verification data yet</Typography>
        <Typography variant="caption" color="text.disabled">
          Verification data will appear here after the first check is completed
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Per-step audit: who performed each check, when, and the signature it
          captured. Shared with the order detail view via OrderVerificationSummary. */}
      <Paper
        data-testid="verification-tab-steps"
        sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Verification History
        </Typography>
        {signaturesFailed ? (
          <Alert severity="error" data-testid="verification-signatures-error">
            Signature records could not be loaded, so this history may be missing
            steps (data verification, dispensing). Reload to try again — do not
            treat missing steps as "never performed".
          </Alert>
        ) : (
          <OrderVerificationSummary order={order} signatures={signatures} />
        )}
      </Paper>

      {/* Summary Header */}
      {drugVerifications.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'success.main', bgcolor: 'rgba(25, 135, 84, 0.04)' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Verification Summary
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {orderNumber ? `Order #${orderNumber}` : ''} {verifiedBy ? `| Verified by ${verifiedBy}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`${drugVerifications.length} drug${drugVerifications.length > 1 ? 's' : ''} verified`}
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
                sx={{ fontWeight: 600 }}
              />
              {verifiedAt && (
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(verifiedAt), 'MMM dd, yyyy h:mm a')}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Per-Drug Verification Cards */}
      {drugVerifications.map((drug, index) => {
        const rxNum = drug.rxNumber || '';
        const sigs = signaturesMap[rxNum] || [];
        const counselFlags = drug.counselFlags || {};
        const activeFlags = Object.entries(counselFlags).filter(([, v]) => v);
        const routing = rxNum ? taskQueueRouting[rxNum] : undefined;

        return (
          <Paper key={index} sx={{ p: 0, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            {/* Drug Header */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: headerStripSurface, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    Rx #{rxNum}
                  </Typography>
                  {drug.din && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      DIN: {drug.din}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="body2" color="text.primary">
                  {drug.medicationName || '—'}
                  {drug.strength ? ` ${drug.strength}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {drug.verifiedDaysSupply && (
                  <Chip label={`${drug.verifiedDaysSupply} days`} size="small" variant="outlined" />
                )}
                <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
              </Stack>
            </Box>

            <Box sx={{ p: 2 }}>
              {/* Signatures Section */}
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                Signatures
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {['data_verification', 'clinical_verification', 'product_verification', 'dispensing'].map((type) => {
                  const sig = sigs.find((s: any) => s.signatureType === type);
                  const isSigned = !!sig;
                  return (
                    <Paper
                      key={type}
                      variant="outlined"
                      sx={{
                        flex: 1,
                        p: 1,
                        textAlign: 'center',
                        borderRadius: 1.5,
                        borderColor: isSigned ? SIGNATURE_COLORS[type] : 'grey.300',
                        bgcolor: isSigned ? `${SIGNATURE_COLORS[type]}08` : 'background.paper',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {SIGNATURE_LABELS[type]}
                      </Typography>
                      {isSigned ? (
                        <>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '1rem',
                              color: SIGNATURE_COLORS[type],
                              fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                              fontStyle: 'italic',
                            }}
                          >
                            {sig.initials}
                          </Typography>
                          {sig.signedAt && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>
                              {format(new Date(sig.signedAt), 'MM/dd h:mma')}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <UncheckedIcon sx={{ fontSize: 20, color: 'grey.300', my: 0.5 }} />
                      )}
                    </Paper>
                  );
                })}
              </Stack>

              {/* Counsel Flags Section */}
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                Counsel Flags
              </Typography>
              {activeFlags.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, gap: 0.5 }}>
                  {Object.entries(COUNSEL_FLAG_LABELS).map(([key, label]) => {
                    const isActive = counselFlags[key];
                    if (!isActive) return null;
                    const isPositive = key === 'didCounsel' || key === 'inPerson' || key === 'viaPhone' || key === 'delivered';
                    return (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        icon={isPositive ? <CheckCircleIcon /> : <CancelIcon />}
                        color={isPositive ? 'success' : 'default'}
                        variant={isPositive ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                  No counsel flags recorded
                </Typography>
              )}

              {/* GP-1439: task-queue routing chosen at clinical verification —
                  the record of WHY this prescription did or did not produce a
                  counselling task. Absent on orders verified before GP-1439. */}
              {routing && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em', mb: 0.5, display: 'block' }}>
                    Task Queue Routing
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                    <Chip
                      label={routing.sendToTaskQueue ? 'Sent to task queue' : 'Not sent to task queue'}
                      size="small"
                      icon={routing.sendToTaskQueue ? <QueueIcon /> : <NoQueueIcon />}
                      color={routing.sendToTaskQueue ? 'info' : 'default'}
                      variant={routing.sendToTaskQueue ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.75rem' }}
                    />
                    {routing.overridden && (
                      <Chip
                        label="Pharmacist changed the default"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Stack>
                  {(routing.decidedBy || routing.decidedAt) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {routing.decidedBy || 'Unknown'}
                      {routing.decidedAt ? ` · ${format(parseISO(routing.decidedAt), 'MMM d, yyyy h:mm a')}` : ''}
                      {routing.policySource ? ` · ${routing.policySource} default` : ''}
                    </Typography>
                  )}
                </Box>
              )}

              {/* ISEC Notes + Summary (documented once at CV, mirrors counselling) */}
              {(() => {
                const notes = drug.clinicalNotes || {};
                const letterNotes = ISEC_QUESTIONS
                  .map((g) => ({ title: g.title, letter: g.letter, value: notes[g.key] }))
                  .filter((n) => n.value && n.value.trim());
                const summary = notes[ISEC_SUMMARY_KEY];
                // Per-ISEC-question documentation points (OCP round 2)
                const points = (drug as any).isecPoints || {};
                const pointGroups = ISEC_QUESTIONS.map(g => ({
                  letter: g.letter, title: g.title,
                  items: g.questions.filter(q => points[q.id]?.checked || (points[q.id]?.notes || '').trim()),
                })).filter(g => g.items.length > 0);
                if (letterNotes.length === 0 && !(summary && summary.trim()) && pointGroups.length === 0) return null;
                return (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                      ISEC Notes
                    </Typography>
                    <Stack spacing={0.75} sx={{ mb: 1 }}>
                      {letterNotes.map((n) => (
                        <Box key={n.letter}>
                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem' }}>
                            {n.letter} · {n.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                            {n.value}
                          </Typography>
                        </Box>
                      ))}
                      {pointGroups.map((g) => (
                        <Box key={`points-${g.letter}`}>
                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem' }}>
                            {g.letter} · {g.title}
                          </Typography>
                          {g.items.map((q) => (
                            <Typography
                              key={q.id}
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}
                            >
                              ✓ {q.text}
                              {(points[q.id]?.notes || '').trim() ? ` — ${points[q.id].notes}` : ''}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                      {summary && summary.trim() && (
                        <Box>
                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem' }}>
                            Summary / Counselling Note
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                            {summary}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </>
                );
              })()}

              {/* Verified By */}
              {drug.verifiedBy && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Verified by <strong>{drug.verifiedBy}</strong>
                    {drug.verifiedAt && ` on ${format(parseISO(drug.verifiedAt), 'MMM dd, yyyy h:mm a')}`}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default VerificationTab;
